import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { COMPARISON_PAGES } from './comparisonData';
import { SITE, ORGANIZATION_SCHEMA, SOFTWARE_SCHEMA } from './seoData';

/* Comparison page layout — distinct from cornerstone:
   hero + summary + comparison table + "when to choose what" + FAQ + CTA */

const C = {
  lime: '#c8f135', limeHover: '#d4f74a',
  bg: '#04050a', surface: 'rgba(255,255,255,0.02)',
  border: 'rgba(255,255,255,0.07)', borderLime: 'rgba(200,241,53,0.22)',
  text: '#f0f0e8', muted: '#3a4830',
};
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, "Cascadia Mono", Consolas, monospace';
const DISP = "'Syne', system-ui, sans-serif";
const sectionPad = 'clamp(80px, 12vw, 140px) clamp(20px, 5vw, 48px)';

const setMeta = (sel, attr, content) => {
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    const m = sel.match(/\[(.+?)="(.+?)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, content);
};
const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
};
const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};
const rmJsonLd = (id) => { const el = document.getElementById(id); if (el) el.remove(); };

const breadcrumbSchema = (page) => ({
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE.domain + '/' },
    { '@type': 'ListItem', position: 2, name: 'Compare', item: SITE.domain + '/compare' },
    { '@type': 'ListItem', position: 3, name: page.breadcrumb, item: `${SITE.domain}/${page.slug}` },
  ],
});

const faqSchema = (page) => ({
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: page.faqs.map(f => ({
    '@type': 'Question', name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

const useSeo = (page) => {
  useEffect(() => {
    if (!page) return;
    const canonical = `${SITE.domain}/${page.slug}`;
    const ogImg = `${SITE.domain}/og-image.png`; // fallback to default; per-page OG TBD
    document.title = page.title;
    setMeta('meta[name="description"]', 'content', page.description);
    setMeta('meta[property="og:title"]', 'content', page.title);
    setMeta('meta[property="og:description"]', 'content', page.description);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', ogImg);
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', page.title);
    setMeta('meta[name="twitter:description"]', 'content', page.description);
    setMeta('meta[name="twitter:image"]', 'content', ogImg);
    setLink('canonical', canonical);
    setJsonLd('ld-org', ORGANIZATION_SCHEMA);
    setJsonLd('ld-software', SOFTWARE_SCHEMA);
    setJsonLd('ld-breadcrumb', breadcrumbSchema(page));
    setJsonLd('ld-faq', faqSchema(page));
    return () => { rmJsonLd('ld-breadcrumb'); rmJsonLd('ld-faq'); };
  }, [page]);
};

const Style = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&display=swap');
    .cmp-fade { opacity: 0; transform: translateY(20px); animation: cmpFade 0.8s cubic-bezier(0.16,1,0.3,1) forwards; }
    @keyframes cmpFade { to { opacity: 1; transform: translateY(0); } }
    .cmp-primary { display: inline-flex; align-items: center; gap: 8px; background: ${C.lime}; color: ${C.bg}; padding: 13px 24px; border-radius: 10px; font-family: ${MONO}; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; box-shadow: 0 0 38px rgba(200,241,53,0.28); transition: transform 0.2s, box-shadow 0.2s; cursor: pointer; border: none; }
    .cmp-primary:hover { transform: translateY(-2px); box-shadow: 0 0 56px rgba(200,241,53,0.45); }
    .cmp-ghost { display: inline-flex; align-items: center; gap: 8px; background: transparent; color: ${C.text}; padding: 12px 22px; border-radius: 10px; font-family: ${MONO}; font-size: 12px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; border: 1px solid ${C.border}; cursor: pointer; }
    .cmp-ghost:hover { border-color: ${C.borderLime}; background: rgba(200,241,53,0.04); }
    details > summary { list-style: none; cursor: pointer; }
    details > summary::-webkit-details-marker { display: none; }
    .cmp-nav-link { font-family: ${MONO}; font-size: 11px; color: ${C.muted}; letter-spacing: 0.04em; text-transform: uppercase; transition: color 0.2s; }
    .cmp-nav-link:hover { color: ${C.lime}; }
  `}</style>
);

const Nav = () => {
  const navigate = useNavigate();
  return (
    <nav style={{ position: 'sticky', top: 0, zIndex: 50, display: 'flex', alignItems: 'center', gap: 32, padding: '14px clamp(20px, 4vw, 48px)', background: 'rgba(4,5,10,0.92)', backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', borderBottom: `1px solid ${C.border}` }} data-testid="cmp-nav">
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: C.lime, color: C.bg, display: 'grid', placeItems: 'center', fontFamily: DISP, fontWeight: 800, fontSize: 14 }}>S</div>
        <span style={{ fontFamily: DISP, fontWeight: 800, fontSize: 15, color: C.text, letterSpacing: '-0.01em' }}>ScalitOS</span>
      </Link>
      <div style={{ display: 'flex', gap: 28, marginRight: 'auto' }} className="hidden md:flex">
        <Link to="/marketing-os" className="cmp-nav-link">Platform</Link>
        <Link to="/campaign-studio" className="cmp-nav-link">Campaigns</Link>
        <Link to="/competitor-analysis" className="cmp-nav-link">Intelligence</Link>
        <Link to="/blog" className="cmp-nav-link">Blog</Link>
      </div>
      <button onClick={() => navigate('/login')} className="cmp-nav-link" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Sign in</button>
      <button onClick={() => navigate('/register')} style={{ background: C.lime, color: C.bg, padding: '9px 16px', borderRadius: 8, fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', border: 'none', cursor: 'pointer' }}>Start free &rarr;</button>
    </nav>
  );
};

const Breadcrumb = ({ label }) => (
  <nav aria-label="Breadcrumb" style={{ padding: '24px clamp(20px, 5vw, 48px) 0', maxWidth: 1200, margin: '0 auto' }}>
    <ol style={{ display: 'flex', gap: 8, fontFamily: MONO, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.muted, listStyle: 'none', padding: 0 }}>
      <li><Link to="/" style={{ color: C.muted, textDecoration: 'none' }}>Home</Link></li>
      <li aria-hidden="true">/</li>
      <li style={{ color: C.lime }}>{label}</li>
    </ol>
  </nav>
);

const Footer = () => (
  <footer style={{ padding: 'clamp(60px, 8vw, 100px) clamp(20px, 5vw, 48px) 60px', borderTop: `1px solid ${C.border}` }}>
    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 40 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: C.lime, color: C.bg, display: 'grid', placeItems: 'center', fontFamily: DISP, fontWeight: 800, fontSize: 14 }}>S</div>
          <span style={{ fontFamily: DISP, fontWeight: 800, fontSize: 16, color: C.text, letterSpacing: '-0.01em' }}>ScalitOS</span>
        </div>
        <p style={{ fontFamily: MONO, fontSize: 11, color: C.muted, lineHeight: 1.8, maxWidth: 240 }}>The marketing operating system for solo founders and lean teams.</p>
      </div>
      {[
        { h: 'Platform', l: [['/marketing-os', 'Marketing OS'], ['/campaign-studio', 'Campaign Studio'], ['/ad-creative-generator', 'Ad Creative'], ['/landing-page-generator', 'Landing Pages'], ['/email-campaigns', 'Email'], ['/competitor-analysis', 'Competitor Intel']] },
        { h: 'Compare', l: [['/scalitos-vs-jasper', 'vs Jasper'], ['/scalitos-vs-copy-ai', 'vs Copy.ai'], ['/scalitos-vs-marketing-agency', 'vs an Agency']] },
        { h: 'Get started', l: [['/register', 'Create account'], ['/login', 'Sign in'], ['/blog', 'Blog']] },
      ].map((col) => (
        <div key={col.h}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 14 }}>{col.h}</p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {col.l.map(([href, label]) => (
              <li key={href}><Link to={href} style={{ fontFamily: MONO, fontSize: 11, color: C.text, textDecoration: 'none', letterSpacing: '0.02em' }}>{label}</Link></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <p style={{ marginTop: 60, fontFamily: MONO, fontSize: 10, color: C.muted, textAlign: 'center', letterSpacing: '0.04em' }}>&copy; {new Date().getFullYear()} ScalitOS</p>
  </footer>
);

const ComparisonPageLayout = ({ slug }) => {
  const navigate = useNavigate();
  const page = COMPARISON_PAGES[slug];
  useSeo(page);

  if (!page) {
    return (
      <div style={{ background: C.bg, color: C.text, minHeight: '100vh', padding: 60 }}>
        <h1>Comparison not found</h1>
        <Link to="/" style={{ color: C.lime }}>Back to home</Link>
      </div>
    );
  }

  return (
    <div data-testid={`comparison-page-${slug}`} style={{ background: C.bg, color: C.text, fontFamily: MONO, minHeight: '100vh' }}>
      <Style />
      <Nav />
      <Breadcrumb label={page.breadcrumb} />

      {/* HERO */}
      <header style={{ padding: 'clamp(60px, 9vw, 110px) clamp(20px, 5vw, 48px) clamp(40px, 6vw, 80px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p className="cmp-fade" style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 18 }}>{page.kicker}</p>
          <h1 className="cmp-fade" style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(38px, 7vw, 80px)', letterSpacing: '-0.04em', lineHeight: 0.98, color: C.text, animationDelay: '120ms' }}>
            <span style={{ display: 'block' }}>{page.h1Top}</span>
            <span style={{ display: 'block', color: C.lime }}>{page.h1Bottom}</span>
          </h1>
          <p className="cmp-fade" style={{ marginTop: 28, fontFamily: MONO, fontSize: 14, lineHeight: 1.9, color: C.muted, maxWidth: 720, animationDelay: '240ms' }}>{page.subhead}</p>
        </div>
      </header>

      {/* SUMMARY */}
      <section style={{ padding: '0 clamp(20px, 5vw, 48px) clamp(40px, 6vw, 60px)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 28, border: `1px solid ${C.borderLime}`, borderRadius: 18, background: 'linear-gradient(135deg, rgba(200,241,53,0.06), rgba(200,241,53,0.01))' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>The short version</p>
          <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 'clamp(18px, 2.6vw, 24px)', lineHeight: 1.5, letterSpacing: '-0.012em', color: C.text }}>{page.summary}</p>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section style={{ padding: sectionPad, background: 'rgba(255,255,255,0.012)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>Side by side</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 52px)', letterSpacing: '-0.025em', lineHeight: 1.05, color: C.text, marginBottom: 40 }}>The feature surface, compared.</h2>
          <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: 'rgba(200,241,53,0.04)' }}>
                  <th style={{ textAlign: 'left', padding: '18px 22px', fontFamily: MONO, fontSize: 10, letterSpacing: '0.18em', color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>Capability</th>
                  <th style={{ textAlign: 'left', padding: '18px 22px', fontFamily: DISP, fontWeight: 700, fontSize: 14, color: C.lime, letterSpacing: '-0.01em', borderBottom: `1px solid ${C.border}` }}>ScalitOS</th>
                  <th style={{ textAlign: 'left', padding: '18px 22px', fontFamily: DISP, fontWeight: 700, fontSize: 14, color: C.text, letterSpacing: '-0.01em', borderBottom: `1px solid ${C.border}` }}>{page.competitor}</th>
                </tr>
              </thead>
              <tbody>
                {page.table.map((row, i) => (
                  <tr key={i} style={{ borderBottom: i < page.table.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ padding: '16px 22px', fontFamily: MONO, fontSize: 12, color: 'rgba(240,240,232,0.65)', letterSpacing: '0.01em' }}>{row.row}</td>
                    <td style={{ padding: '16px 22px', fontFamily: MONO, fontSize: 12, color: C.text }}>{row.scalitos}</td>
                    <td style={{ padding: '16px 22px', fontFamily: MONO, fontSize: 12, color: 'rgba(240,240,232,0.5)' }}>{row.competitor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* WHEN TO CHOOSE WHAT */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>An honest framework</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 52px)', letterSpacing: '-0.025em', lineHeight: 1.05, color: C.text, marginBottom: 40 }}>When to choose what.</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {page.whenToChoose.map((c, i) => (
              <article key={i} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14, padding: '24px 26px', border: `1px solid ${C.border}`, borderRadius: 14, background: C.surface }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999, background: c.pick === 'ScalitOS' ? C.lime : 'rgba(255,255,255,0.06)', color: c.pick === 'ScalitOS' ? C.bg : C.text, fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Pick: {c.pick}</span>
                  <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 17, color: C.text, letterSpacing: '-0.01em', lineHeight: 1.3 }}>{c.who}</h3>
                </div>
                <p style={{ fontFamily: MONO, fontSize: 12, lineHeight: 1.85, color: 'rgba(240,240,232,0.6)' }}>{c.why}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 48px)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 36px', border: `1px solid ${C.borderLime}`, borderRadius: 20, background: 'linear-gradient(135deg, rgba(200,241,53,0.06), rgba(200,241,53,0.01))', textAlign: 'center' }}>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4.4vw, 44px)', letterSpacing: '-0.025em', lineHeight: 1.1, color: C.text, marginBottom: 14 }}>Try it free. Decide on the data.</h2>
          <p style={{ fontFamily: MONO, fontSize: 13, color: C.muted, marginBottom: 28, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.8 }}>Run one campaign through ScalitOS in your first week. If it doesn\u2019t move the needle, keep what you\u2019re doing. No card required.</p>
          <button onClick={() => navigate('/register')} className="cmp-primary" data-testid={`cmp-cta-${slug}`}>Start free &rarr;</button>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: sectionPad, background: 'rgba(255,255,255,0.012)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>Frequently asked</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 52px)', lineHeight: 1.05, letterSpacing: '-0.025em', color: C.text, marginBottom: 36 }}>The honest answers.</h2>
          <div>
            {page.faqs.map((f, i) => (
              <details key={i} style={{ borderTop: `1px solid ${C.border}`, padding: '22px 0' }}>
                <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                  <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 18, color: C.text, letterSpacing: '-0.01em' }}>{f.q}</h3>
                  <span style={{ fontFamily: DISP, fontWeight: 700, fontSize: 22, color: C.lime, lineHeight: 1 }}>+</span>
                </summary>
                <p style={{ marginTop: 14, fontFamily: MONO, fontSize: 13, lineHeight: 1.85, color: 'rgba(240,240,232,0.65)', maxWidth: 760 }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED COMPARISONS */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>More comparisons</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(24px, 4vw, 36px)', letterSpacing: '-0.025em', lineHeight: 1.1, color: C.text, marginBottom: 28 }}>See how ScalitOS compares to other choices.</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
            {Object.values(COMPARISON_PAGES).filter(p => p.slug !== slug).map(p => (
              <Link key={p.slug} to={`/${p.slug}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', border: `1px solid ${C.border}`, borderRadius: 12, textDecoration: 'none', background: C.surface, transition: 'border-color 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderLime; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; }}>
                <span style={{ fontFamily: DISP, fontWeight: 700, fontSize: 14, color: C.text, letterSpacing: '-0.01em' }}>ScalitOS vs {p.competitor}</span>
                <span style={{ color: C.lime, fontFamily: MONO, fontSize: 14 }}>&rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComparisonPageLayout;
